import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { _resultOf, type ExceptionResult } from '@core/util/async.util'
import { IS_A_STRING_AND_NOT_EMPTY, IS_NOT_SET, IS_SET } from '@core/util/check.util'
import { TO_STRING } from '@core/util/string.util'
import { environment } from '@env/environment'
import { lastValueFrom, Observable, of } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'

export interface UrlParams { url?: string, api?: string, path?: string }

export interface GetParams extends UrlParams { options?: any, params?: any, q?: any }
export interface PostParams extends GetParams { body?: any }
export interface PutParams extends PostParams { }
export interface DeleteParams extends GetParams { }
export interface GetCsvParams extends GetParams { pageName?: string }
export interface GetPdfParams extends GetParams { pageName?: string }

@Injectable({ providedIn: 'root' })
export class HttpService {
  HttpClient = inject(HttpClient)

  apiEndpoint = environment.apiEndpoint

  typeToExt: Record<string, string> = {
    'application/csv': '.csv',
    'application/pdf': '.pdf'
  }

  generateUrl (_: UrlParams): string {
    return _.url ?? `${_.api ?? this.apiEndpoint}${TO_STRING(_.path)}`
  }

  async structure (request: Observable<any> | Promise<any>): Promise<ExceptionResult> {
    const res = await _resultOf((request instanceof Observable) ? lastValueFrom(request) : request)
    return res
  }

  async throwIfError (request: Promise<ExceptionResult>): Promise<ExceptionResult> {
    const response = await request

    if (IS_SET(response.exception)) {
      throw response.exception
    }

    return response
  }

  async get (_: GetParams): Promise<ExceptionResult> {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    return await this.structure(this.HttpClient.get(this.generateUrl(_), options))
  }

  async post (_: PostParams): Promise<ExceptionResult> {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    return await this.structure(this.HttpClient.post(this.generateUrl(_), _.body, options))
  }

  async put (_: PutParams): Promise<ExceptionResult> {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    return await this.structure(this.HttpClient.put(this.generateUrl(_), _.body, options))
  }

  async delete (_: DeleteParams): Promise<ExceptionResult> {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    return await this.structure(this.HttpClient.delete(this.generateUrl(_), options))
  }

  fileDownLoad (_: { data: any, type: string, pageName?: string }): void {
    const { data, type } = _
    const blob = new Blob([data], { type })
    const blobUrl = window.URL.createObjectURL(blob)
    const domId = 'dom_a_file_download'
    const domAFound = document.getElementById(domId)
    const domA: HTMLElement = domAFound ?? document.createElement('a')
    if (IS_NOT_SET(domAFound)) {
      domA.id = domId
      domA.setAttribute('style', 'display:none')
      document.body.appendChild(domA)
    }
    domA.setAttribute('href', blobUrl)
    domA.setAttribute(
      'download',
      `${+(new Date())}${IS_A_STRING_AND_NOT_EMPTY(_.pageName) ? `-${TO_STRING(_.pageName)}` : ''}-export${TO_STRING(this.typeToExt[type])}`
    )
    domA.click()
    document.body.removeChild(domA)
    window.URL.revokeObjectURL(blobUrl)
  }

  getCsv (_: GetCsvParams): any {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    this
      .HttpClient
      .get(this.generateUrl(_), { responseType: 'arraybuffer', ...(options ?? {}) })
      .pipe(
        tap(res => {
          this.fileDownLoad({ data: res, type: 'application/csv', pageName: _.pageName })
        }),
        catchError(err => {
          console.error(err)
          return of(err)
        })
      )
      .subscribe()
  }

  getPdf (_: GetPdfParams): any {
    const options: Record<string, any> = _.options ?? {}

    if (IS_SET(_.params)) {
      options.params = _.params
    }

    if (IS_SET(_.q)) {
      options.params = { ...(options.params ?? {}), q: JSON.stringify(_.q) }
    }

    this
      .HttpClient
      .get(this.generateUrl(_), { responseType: 'arraybuffer', ...(options ?? {}) })
      .pipe(
        tap(res => { this.fileDownLoad({ data: res, type: 'application/pdf', pageName: _.pageName }) }),
        catchError(err => {
          console.error(err)
          return of(err)
        })
      )
      .subscribe()
  }
}
